import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square, Volume2 } from "lucide-react";

// Client-side text-to-speech using the browser Web Speech API. No external service.
export function AudioReader({ text, testid = "audio-reader" }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [status, setStatus] = useState("idle"); // idle | playing | paused
  const utterRef = useRef(null);

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    return (
      voices.find((v) => v.lang === "en-IN") ||
      voices.find((v) => v.lang && v.lang.startsWith("en")) ||
      voices[0] ||
      null
    );
  };

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus("idle");
  }, [supported]);

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);
  // reset when the article text changes
  useEffect(() => { stop(); }, [text, stop]);

  const play = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice();
    if (v) u.voice = v;
    u.lang = v?.lang || "en-IN";
    u.rate = 1;
    u.pitch = 1;
    u.onend = () => setStatus("idle");
    u.onerror = () => setStatus("idle");
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setStatus("playing");
  };

  const toggle = () => {
    if (status === "idle") { play(); return; }
    if (status === "playing") { window.speechSynthesis.pause(); setStatus("paused"); return; }
    if (status === "paused") { window.speechSynthesis.resume(); setStatus("playing"); }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-3 border border-border bg-card px-4 py-3" data-testid={testid}>
      <Volume2 className="h-4 w-4 text-cyan" />
      <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {status === "idle" ? "Listen to this article" : status === "playing" ? "Playing…" : "Paused"}
      </span>
      <Button size="sm" onClick={toggle} data-testid={`${testid}-toggle`}
        className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90">
        {status === "playing" ? <><Pause className="mr-1.5 h-4 w-4" /> Pause</> : status === "paused" ? <><Play className="mr-1.5 h-4 w-4" /> Resume</> : <><Play className="mr-1.5 h-4 w-4" /> Listen</>}
      </Button>
      {status !== "idle" && (
        <Button size="sm" variant="outline" onClick={stop} data-testid={`${testid}-stop`}
          className="rounded-none border-border font-mono text-xs uppercase tracking-widest hover:border-destructive hover:text-destructive">
          <Square className="mr-1.5 h-3.5 w-3.5" /> Stop
        </Button>
      )}
    </div>
  );
}
